import { useReaderAppreciation } from "./ReaderAppreciation";
import { readerPrice } from "../../utils/readerAppreciation";
import { displayLabel } from "../../utils/displayLabel";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CheckoutMobile.css";
import { rememberCheckout } from './checkoutReturn';
import { getCurrentReader } from "../../auth/getCurrentReader";

const SHIPPING_RATES_URL =
  process.env.REACT_APP_SHIPPING_RATES_URL ||
  "https://f91ubu4sc3.execute-api.us-west-2.amazonaws.com/default/get-shipping-rates";
const STRIPE_LAMBDA_URL =
  process.env.REACT_APP_STRIPE_LAMBDA_URL ||
  process.env.REACT_APP_CHECKOUT_SESSION_URL ||
  "https://b7zlmugmb8.execute-api.us-west-2.amazonaws.com/default/createCheckoutSession";

const INITIAL_ADDRESS = {
  fullName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

const toCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `$${amount.toFixed(2)}`;
};

const isEbookFormat = (format = {}) =>
  String(format?.type || "")
    .toLowerCase()
    .replace(/\s+/g, "") === "ebook";

const mapAddressForRates = (address) => ({
  name: address.fullName,
  street1: address.addressLine1,
  street2: address.addressLine2,
  city: address.city,
  state: address.state,
  zip: address.zip,
  country: address.country,
});

const buildShippingItems = (items = []) => {
  return items.flatMap((item) => {
    const shipping = item?.shipping || {};
    const quantity = Math.max(1, Number(item?.quantity || 1));

    const shippingItem = {
      weight:
        Number(shipping?.weight ?? item?.weight) || 1,

      length:
        Number(shipping?.length ?? item?.length) || undefined,

      width:
        Number(shipping?.width ?? item?.width) || undefined,

      height:
        Number(shipping?.height ?? item?.height) || undefined,

      mediaMailEligible:
        shipping?.mediaMailEligible ??
        item?.mediaMailEligible ??
        false,

      hasExtras:
        shipping?.hasExtras ??
        item?.hasExtras ??
        false,
    };

    return Array.from({ length: quantity }, () => ({
      ...shippingItem,
    }));
  });
};

export default function CheckoutModal({
  isOpen,
  onClose,
  book,
  format,
  bookslug,
  cartItems = [],
}) {
  const [address, setAddress] = useState(INITIAL_ADDRESS);
  const shippingRequestVersion = useRef(0);
  const dialogRef = useRef(null);
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [signatureName, setSignatureName] = useState("");
  const [noPersonalization, setNoPersonalization] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState("");
  const [formError, setFormError] = useState("");
  const [reader, setReader] = useState(null);
  const appreciation = useReaderAppreciation(window.location.hostname, reader);
  const effectivePrice = item => readerPrice(item?.price || 0, item?.format, appreciation.participating, !!reader?.readerSub) || 0;
  const [checkingReader, setCheckingReader] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setCheckingReader(true);
    getCurrentReader().then((current) => {
      if (active) { setReader(current); setCheckingReader(false); }
    }).catch(() => {
      if (active) { setReader(null); setCheckingReader(false); }
    });
    return () => { active = false; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dialog = dialogRef.current;
    dialog.focus();
    const handleKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])')];
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
    };
    dialog.addEventListener('keydown', handleKey);
    return () => { dialog.removeEventListener('keydown', handleKey); document.body.style.overflow = overflow; if (previous?.isConnected) previous.focus(); };
  }, [isOpen, onClose]);

  const isCartCheckout = Array.isArray(cartItems) && cartItems.length > 0;

  const checkoutItems = useMemo(() => {
    if (isCartCheckout) {
      return cartItems.map((item) => ({
        ...item,
        quantity: Math.max(1, Number(item?.quantity || 1)),
      }));
    }

    if (!book || !format) return [];

    return [
      {
        cartItemId: `${book?.slug || bookslug || "book"}::${format?.type || "format"}::${
          format?.variationName ||
          format?.selectedVariation?.name ||
          "standard"
        }`,

        bookSlug: book?.slug || bookslug,
        title: book?.title || "",
        authorName: book?.authorName || "",
        format: format?.type || "",
        variationName:
          format?.variationName ||
          format?.selectedVariation?.name ||
          "",

        price: Number(format?.price || 0),
        quantity: 1,

        shipping: format?.shipping || {},

        weight: format?.weight,
        length: format?.length,
        width: format?.width,
        height: format?.height,

        mediaMailEligible:
          format?.shipping?.mediaMailEligible ??
          format?.mediaMailEligible ??
          false,
      },
    ];
  }, [isCartCheckout, cartItems, book, format, bookslug]);

  const physicalItems = useMemo(
  () =>
    checkoutItems.filter(
      (item) =>
        String(item?.format || "")
          .toLowerCase()
          .replace(/\s+/g, "") !== "ebook"
    ),
  [checkoutItems]
);

const hasPhysicalItems = physicalItems.length > 0;

const allPhysicalItemsFreeShipping =
  hasPhysicalItems &&
  physicalItems.every(
    (item) => item?.shipping?.freeShipping === true
  );

  const isEbook =
  checkoutItems.length > 0 &&
  physicalItems.length === 0;

  const hasFreeShipping = allPhysicalItemsFreeShipping;

  useEffect(() => {
    if (!isOpen) return;

    setAddress(INITIAL_ADDRESS);
    shippingRequestVersion.current += 1;
    setShippingRates([]);
    setSelectedRate(null);
    setSignatureName("");
    setNoPersonalization(false);
    setShippingLoading(false);
    setShippingError("");
    setFormError("");
  }, [isOpen, format]);

  if (!isOpen || checkoutItems.length === 0) return null;

  const handleAddressChange = (field, value) => {
    shippingRequestVersion.current += 1;
    setAddress((prev) => ({ ...prev, [field]: value }));
    setShippingRates([]);
    setSelectedRate(null);
    setShippingLoading(false);
    setShippingError("");
  };

  const requiredAddressFields = ["fullName", "addressLine1", "city", "state", "zip", "country"];

  const hasMissingAddressField = requiredAddressFields.some(
    (field) => !String(address[field] || "").trim()
  );

  const handleGetShippingRates = async () => {
    if (!hasPhysicalItems) return;

    if (hasMissingAddressField) {
      setShippingError("Please complete all required shipping fields before fetching shipping options.");
      setShippingRates([]);
      setSelectedRate(null);
      return;
    }

    setShippingLoading(true);
    setShippingError("");
    const requestVersion = ++shippingRequestVersion.current;
    setSelectedRate(null);

    try {
      const response = await fetch(SHIPPING_RATES_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: mapAddressForRates(address),
          items: buildShippingItems(physicalItems),
          authorSubdomain: window.location.host,
        }),
      });

      const data = await response.json();
      if (requestVersion !== shippingRequestVersion.current) return;
      if (!response.ok) {
        throw new Error(data?.error || "Unable to fetch shipping rates.");
      }

      const rates = Array.isArray(data?.rates) ? data.rates : [];
      setShippingRates(rates);

      if (hasFreeShipping) {
        const groundAdvantageRate = rates.find(
          (rate) => rate?.serviceToken === "usps_ground_advantage"
        );

        if (groundAdvantageRate) {
          setSelectedRate(groundAdvantageRate);
        } else {
          setSelectedRate(null);
          setShippingError(
            "USPS Ground Advantage is not available for this address."
          );
        }
      } else {
        setSelectedRate(null);
      }

      if (!rates.length) {
        setShippingError("No shipping options available for this address.");
      }
    } catch (error) {
      if (requestVersion !== shippingRequestVersion.current) return;
      console.error("Shipping rates error:", error);
      setShippingRates([]);
      setSelectedRate(null);
      setShippingError(error?.message || "Unable to fetch shipping rates.");
    } finally {
      if (requestVersion === shippingRequestVersion.current) setShippingLoading(false);
    }
  };

  const handleContinueToPayment = async () => {
    if (paymentLoading || checkingReader) return;
    setFormError("");

    if (hasPhysicalItems && hasMissingAddressField) {
      setFormError("Please complete your shipping address before continuing.");
      return;
    }

    if (hasPhysicalItems && !selectedRate) {
      setFormError("Please choose a shipping option before continuing.");
      return;
    }

    setPaymentLoading(true);
    try {
      const currentReader = await getCurrentReader();
      if (reader && (!currentReader?.idToken || currentReader.readerSub !== reader.readerSub)) {
        throw new Error("We could not verify your reader session. Please close checkout and try again before placing your order.");
      }
      const shippingCost = hasFreeShipping
        ? 0
        : Number(selectedRate?.amount || 0);

      const merchandiseTotal = checkoutItems.reduce(
        (total, item) =>
          total +
          effectivePrice(item) * Number(item?.quantity || 1),
        0
      );

      const checkoutPayload = {
        ...(currentReader?.idToken ? { readerSub: currentReader.readerSub } : {}),
        domain: window.location.origin,

        // Keep author information at the top level for the connected Stripe account.
        authorName:
          checkoutItems[0]?.authorName ||
          book?.authorName ||
          "",

        items: checkoutItems.map((item) => ({
          title: item?.title || "",
          authorName:
            item?.authorName ||
            book?.authorName ||
            "",

          format: item?.format || "",
          variation:
            item?.variationName ||
            "",

          price: Number(item?.price || 0),
          quantity: Number(item?.quantity || 1),

          bookSlug:
            item?.bookSlug ||
            "",

          shipping: item?.shipping || {},

          package: {
            weight: Number(
              item?.shipping?.weight ??
              item?.weight ??
              0
            ),
            length: Number(
              item?.shipping?.length ??
              item?.length ??
              0
            ),
            width: Number(
              item?.shipping?.width ??
              item?.width ??
              0
            ),
            height: Number(
              item?.shipping?.height ??
              item?.height ??
              0
            ),
          },
        })),

        shippingCost,
        freeShipping: hasFreeShipping,
        totalAmount: merchandiseTotal + shippingCost,

        authorSubdomain: window.location.host,
        selectedShippingRate: selectedRate,
        shippingAddress: address,

        signatureName:
          noPersonalization
            ? ""
            : signatureName.trim(),
      };

      const response = await fetch(STRIPE_LAMBDA_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentReader?.idToken ? { Authorization: `Bearer ${currentReader.idToken}` } : {}),
        },
        body: JSON.stringify(checkoutPayload),
      });

      const data = await response.json();

      if (response.ok && data?.url) {
        rememberCheckout(data, checkoutItems, isCartCheckout);
        window.location.href = data.url;
      } else {
        throw new Error(data?.error || "Checkout failed. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setFormError(error.message || "Checkout failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="checkout-heading" tabIndex={-1} className="bookish-checkout max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-bookish-purple/20 bg-[#F8F8F8] shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-bookish-purple/10 bg-[#F8F8F8] px-5 py-4">
          <h2 id="checkout-heading" className="text-xl font-semibold text-bookish-purple">Checkout</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-bookish-purple/20 px-3 py-1 text-sm font-medium text-bookish-purple hover:bg-bookish-purple/5"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-5">
          <p className="text-sm text-bookish-purple">
            {checkingReader ? "Checking your reader account..." : reader
              ? `Signed in as ${reader.email || "a reader"}.`
              : <>Checking out as a guest. <a className="underline" href={`/loginreader?returnTo=${encodeURIComponent(window.location.href)}`}>Sign in to your reader account</a> before ordering.</>}
          </p>
          <section className="rounded-xl border border-bookish-purple/20 bg-white p-4">
            <h3 className="mb-3 text-base font-semibold text-bookish-purple">
              Order Summary
            </h3>

            <div className="space-y-3">
              {checkoutItems.map((item) => {
                const quantity = Number(item?.quantity || 1);
                const price = effectivePrice(item);

                return (
                  <div
                    key={item.cartItemId}
                    className="flex items-start justify-between gap-4 border-b border-bookish-purple/10 pb-3 last:border-b-0 last:pb-0"
                  >
                    <div className="text-sm text-bookish-purple/80">
                      <p className="font-semibold text-bookish-purple">
                        {displayLabel(item.title || "Untitled")}
                      </p>

                      <p>
                        {displayLabel(item.format || "—")}
                        {item.variationName ? ` — ${displayLabel(item.variationName)}` : ""}
                      </p>

                      {quantity > 1 && (
                        <p className="text-bookish-purple/60">
                          Qty: {quantity}
                        </p>
                      )}
                    </div>

                    <span className="whitespace-nowrap text-sm font-semibold text-bookish-purple">
                      {toCurrency(price * quantity)}
                    </span>
                  </div>
                );
              })}

              {hasPhysicalItems && (
                <p className="pt-1 text-sm text-bookish-purple/80">
                  <span className="font-medium text-bookish-purple">
                    Shipping:
                  </span>{" "}
                  {hasFreeShipping ? "Free Shipping" : "Calculated below"}
                </p>
              )}
            </div>
          </section>

          {hasPhysicalItems && (
            <>
              <section className="rounded-xl border border-bookish-purple/20 bg-white p-4">
                <h3 className="mb-3 text-base font-semibold text-bookish-purple">Shipping Address</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="checkout-address-field" htmlFor="checkout-fullName"><span>Full Name</span><input id="checkout-fullName" autoComplete="name" type="text" value={address.fullName} onChange={(e) => handleAddressChange("fullName", e.target.value)} placeholder="Full Name" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple" /></label>
                  <label className="checkout-address-field" htmlFor="checkout-addressLine1"><span>Address Line 1</span><input id="checkout-addressLine1" autoComplete="address-line1" type="text" value={address.addressLine1} onChange={(e) => handleAddressChange("addressLine1", e.target.value)} placeholder="Address Line 1" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple" /></label>
                  <label className="checkout-address-field" htmlFor="checkout-addressLine2"><span>Address Line 2 (optional)</span><input id="checkout-addressLine2" autoComplete="address-line2" type="text" value={address.addressLine2} onChange={(e) => handleAddressChange("addressLine2", e.target.value)} placeholder="Address Line 2 (optional)" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple md:col-span-2" /></label>
                  <label className="checkout-address-field" htmlFor="checkout-city"><span>City</span><input id="checkout-city" autoComplete="address-level2" type="text" value={address.city} onChange={(e) => handleAddressChange("city", e.target.value)} placeholder="City" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple" /></label>
                  <label className="checkout-address-field" htmlFor="checkout-state"><span>State</span><input id="checkout-state" autoComplete="address-level1" type="text" value={address.state} onChange={(e) => handleAddressChange("state", e.target.value)} placeholder="State" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple" /></label>
                  <label className="checkout-address-field" htmlFor="checkout-zip"><span>ZIP</span><input id="checkout-zip" autoComplete="postal-code" type="text" value={address.zip} onChange={(e) => handleAddressChange("zip", e.target.value)} placeholder="ZIP" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple" /></label>
                  <label className="checkout-address-field" htmlFor="checkout-country"><span>Country</span><input id="checkout-country" autoComplete="country" type="text" value={address.country} onChange={(e) => handleAddressChange("country", e.target.value)} placeholder="Country" className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple" /></label>
                </div>
              </section>

              <section className="rounded-xl border border-bookish-purple/20 bg-white p-4">
                <h3 className="mb-3 text-base font-semibold text-bookish-purple">Shipping Options</h3>

                {hasFreeShipping && (
                  <p className="mb-3 rounded-md bg-green-50 px-3 py-2 text-sm font-semibold text-green-800">
                    Free Shipping — USPS Ground Advantage will be used at no cost to you.
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleGetShippingRates}
                  disabled={shippingLoading}
                  className="mb-3 rounded-md border border-bookish-purple bg-white px-4 py-2 text-sm font-semibold text-bookish-purple transition hover:bg-bookish-purple/10 disabled:opacity-60"
                >
                  {shippingLoading ? "Loading shipping options..." : "Get Shipping Rates"}
                </button>

                {hasFreeShipping ? (
                  selectedRate ? (
                    <div className="rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple">
                      <span className="font-semibold">Free Shipping</span>
                      {" — USPS Ground Advantage"}
                    </div>
                  ) : (
                    <p className="text-sm text-bookish-purple/70">
                      {shippingError || "Enter your address and get the free shipping rate."}
                    </p>
                  )
                ) : shippingRates.length > 0 ? (
                  <div className="space-y-2">
                    {shippingRates.map((rate) => {
                      const label = `${rate?.provider || "USPS"} ${
                        rate?.service || "Shipping"
                      } — ${toCurrency(rate?.amount)}`;

                      return (
                        <label
                          key={rate.id || `${rate.provider}-${rate.service}-${rate.amount}`}
                          className="flex cursor-pointer items-center gap-2 rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple"
                        >
                          <input
                            type="radio"
                            name="shipping-rate"
                            checked={selectedRate?.id === rate.id}
                            onChange={() => setSelectedRate(rate)}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-bookish-purple/70">
                    {shippingError || "No shipping options loaded yet."}
                  </p>
                )}
              </section>
            </>
          )}

          {hasPhysicalItems && (
            <section className="rounded-xl border border-bookish-purple/20 bg-white p-4">
              <h3 className="mb-3 text-base font-semibold text-bookish-purple">Personalization (Optional)</h3>
              <label htmlFor="checkout-signature" className="mb-2 block text-sm font-medium text-bookish-purple">
                Who should this book be signed to?
              </label>
              <input
                id="checkout-signature"
                type="text"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                disabled={noPersonalization}
                placeholder="e.g. Jane"
                className="w-full rounded-md border border-bookish-purple/20 bg-white px-3 py-2 text-sm text-bookish-purple disabled:bg-gray-100 disabled:text-bookish-purple/50"
              />
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-bookish-purple">
                <input
                  type="checkbox"
                  checked={noPersonalization}
                  onChange={(e) => setNoPersonalization(e.target.checked)}
                />
                No personalization
              </label>
            </section>
          )}

          <section className="checkout-totals rounded-xl border border-bookish-purple/20 bg-white p-4" aria-label="Payment estimate" aria-live="polite">
            <div><span>Book subtotal</span><strong>{toCurrency(checkoutItems.reduce((sum, item) => sum + effectivePrice(item) * Number(item.quantity || 1), 0))}</strong></div>
            {appreciation.participating && reader?.readerSub && <p className="text-sm">Eligible Reader Appreciation discounts are included.</p>}
            <div><span>Shipping</span><strong>{!hasPhysicalItems ? 'Not required' : hasFreeShipping ? 'Free' : selectedRate ? toCurrency(selectedRate.amount) : 'Choose a shipping option'}</strong></div>
            {(!hasPhysicalItems || hasFreeShipping || selectedRate) && <div><span>Total before tax</span><strong>{toCurrency(checkoutItems.reduce((sum, item) => sum + effectivePrice(item) * Number(item.quantity || 1), 0) + (hasPhysicalItems && !hasFreeShipping ? Number(selectedRate?.amount || 0) : 0))}</strong></div>}
            <p className="text-sm">Any applicable tax and the final total are shown on the payment screen before you pay.</p>
          </section>
          {formError && <p role="alert" className="text-sm font-medium text-red-600">{formError}</p>}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleContinueToPayment}
              disabled={checkingReader || paymentLoading}
              className="rounded-md bg-bookish-royal px-5 py-2 text-base font-semibold text-white transition hover:bg-bookish-purple"
            >
              {paymentLoading ? "Opening payment..." : "Continue to Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
